import { ChevronLeft, Ellipsis } from "lucide-react";
import { DropdownMenu as M } from "radix-ui";
import { cn } from "../lib/cn";
import { foldLevels, MAX_VISIBLE_LEVELS } from "../lib/navTrail";
import { Skeleton } from "./Skeleton";

// The crumbs at the top of a page (ADR 0030, DESIGN.md "Navigation and breadcrumbs"): the levels
// above the page, then the page itself. Quiet: `caption` in `ink-3`; each level above the page is
// a link, the page itself is not. A chevron before them goes back one level. Long labels
// truncate; past three levels the middle ones fold into a "…" menu. The app shell renders it
// from the navigation trail; pages never build it by hand.

export interface Crumb {
  /** Its name; null while it loads (a skeleton). */
  label: string | null;
  /** Opens that level. Absent on the page itself. */
  onClick?: () => void;
}

export interface BreadcrumbsProps {
  /** The levels above the page, top first. */
  above?: Crumb[];
  /** The page itself, last and not a link; absent when the page's header names it. */
  current?: Crumb | null;
  /** Goes back one level (the chevron); defaults to the last level above. */
  onBack?: () => void;
  /** The level the chevron goes to, for screen readers ("Back to <it>"), when not the last shown. */
  backLabel?: string | null;
  /** Most levels above the page shown before the middle ones fold. */
  maxLevels?: number;
  className?: string;
}

const FOCUS = "focus-visible:shadow-[0_0_0_2px_var(--vd-focus)] focus-visible:outline-none";

function Label({ c, max }: { c: Crumb; max: string }) {
  if (c.label === null) return <Skeleton className="h-3 w-16" />;
  return (
    <span className={cn("block truncate", max)} title={c.label}>
      {c.label}
    </span>
  );
}

/**
 * "‹ Analytics / Calls & Dispositions / Bel Merch & Design". Renders nothing when there is no
 * level above the page (a tab's own page, opened from the sidebar).
 */
export function Breadcrumbs({
  above = [],
  current = null,
  onBack,
  backLabel,
  maxLevels = MAX_VISIBLE_LEVELS,
  className,
}: BreadcrumbsProps) {
  const parent = above.at(-1);
  const back = onBack ?? parent?.onClick;
  if (!parent && !back) return null;
  const backTo = backLabel ?? parent?.label ?? null;
  const { head, folded, tail } = foldLevels(above, maxLevels);
  const link = (c: Crumb, i: number) => (
    <li key={`l-${i}`} className="flex min-w-0 shrink-0 items-center">
      {i > 0 ? (
        <span aria-hidden className="px-0.5 text-ink-4">
          /
        </span>
      ) : null}
      {c.onClick ? (
        <button
          type="button"
          onClick={c.onClick}
          className={cn(
            "max-w-[24ch] rounded-sm px-1 text-ink-3 hover:text-ink hover:underline",
            FOCUS,
          )}
        >
          <Label c={c} max="max-w-[24ch]" />
        </button>
      ) : (
        <span className="px-1">
          <Label c={c} max="max-w-[24ch]" />
        </span>
      )}
    </li>
  );
  return (
    <nav
      aria-label="Breadcrumb"
      className={cn("flex min-h-7 min-w-0 items-center text-caption text-ink-3", className)}
    >
      {back ? (
        <button
          type="button"
          aria-label={backTo ? `Back to ${backTo}` : "Back"}
          onClick={back}
          className={cn(
            "press -ml-1.5 flex size-7 shrink-0 items-center justify-center rounded-md text-ink-2",
            "hover:bg-fill-2 hover:text-ink [&_svg]:size-4",
            FOCUS,
          )}
        >
          <ChevronLeft aria-hidden />
        </button>
      ) : null}
      <ol className="flex min-w-0 items-center">
        {head.map((c, i) => link(c, i))}
        {folded.length > 0 ? (
          <li className="flex shrink-0 items-center">
            <span aria-hidden className="px-0.5 text-ink-4">
              /
            </span>
            <M.Root>
              <M.Trigger asChild>
                <button
                  type="button"
                  aria-label={`${folded.length} more levels`}
                  className={cn(
                    "flex h-5 items-center rounded-sm px-1 text-ink-3 hover:bg-fill-2 hover:text-ink [&_svg]:size-4",
                    FOCUS,
                  )}
                >
                  <Ellipsis aria-hidden />
                </button>
              </M.Trigger>
              <M.Portal>
                <M.Content
                  align="start"
                  sideOffset={4}
                  collisionPadding={8}
                  className="z-50 min-w-[180px] max-w-[320px] rounded-md bg-overlay p-1 text-ink shadow-popover"
                >
                  {folded.map((c, i) => (
                    <M.Item
                      // biome-ignore lint/suspicious/noArrayIndexKey: levels are positional
                      key={i}
                      onSelect={() => c.onClick?.()}
                      className="flex h-8 cursor-default select-none items-center rounded-sm px-2.5 text-body outline-none data-[highlighted]:bg-fill-2"
                    >
                      <span className="truncate">{c.label ?? "…"}</span>
                    </M.Item>
                  ))}
                </M.Content>
              </M.Portal>
            </M.Root>
          </li>
        ) : null}
        {tail.map((c, i) => link(c, head.length + folded.length + i))}
        {current && parent ? (
          <li className="flex min-w-0 shrink items-center">
            <span aria-hidden className="px-0.5 text-ink-4">
              /
            </span>
            <span aria-current="page" className="min-w-0 px-1">
              <Label c={current} max="max-w-[40ch]" />
            </span>
          </li>
        ) : null}
      </ol>
    </nav>
  );
}
